import { useEffect, useMemo, useState } from "react";
import { Mail, Send, Eye, FileText, Filter, Search, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("confirmph_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function apiGet(path) {
  const r = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

async function apiPost(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(t || `${r.status} ${r.statusText}`);
  }
  return r.json();
}

const PREVIEW_MODES = [
  { id: "html", label: "HTML", icon: Eye },
  { id: "text", label: "Plain text", icon: FileText },
  { id: "json", label: "Subject + placeholders", icon: Mail },
];

export default function AdminEmailPreview() {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [q, setQ] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [previewMode, setPreviewMode] = useState("html");
  const [details, setDetails] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState("");
  const [loading, setLoading] = useState(false);

  // test-send form
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [allowMarketing, setAllowMarketing] = useState(false);

  useEffect(() => {
    apiGet("/admin/dev/email-preview")
      .then((d) => { setCatalog(d); if (d.templates?.length) setSelectedKey(d.templates[0].key); })
      .catch((e) => setError(String(e.message || e)));
  }, []);

  useEffect(() => {
    if (!selectedKey) return;
    setLoading(true);
    Promise.all([
      apiGet(`/admin/dev/email-preview/${encodeURIComponent(selectedKey)}`),
      fetch(`${API}/admin/dev/email-preview/${encodeURIComponent(selectedKey)}/html`,
            { headers: authHeaders() }).then((r) => r.text()),
    ])
      .then(([d, html]) => { setDetails(d); setHtmlPreview(html); })
      .catch((e) => setError(String(e.message || e)))
      .finally(() => setLoading(false));
    setSendResult(null);
  }, [selectedKey]);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    const needle = q.trim().toLowerCase();
    return catalog.templates.filter((t) => {
      if (filterCat !== "all" && t.category !== filterCat) return false;
      if (!needle) return true;
      return t.key.toLowerCase().includes(needle)
            || t.label.toLowerCase().includes(needle);
    });
  }, [catalog, filterCat, q]);

  async function doSend() {
    if (!sendTo) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await apiPost("/admin/dev/email-test-send", {
        template_key: selectedKey,
        to: sendTo,
        allow_marketing: allowMarketing,
      });
      setSendResult({ ok: r.ok, ...r });
    } catch (e) {
      setSendResult({ ok: false, error_message: String(e.message || e) });
    } finally {
      setSending(false);
    }
  }

  if (error) {
    return (
      <div className="p-6" data-testid="email-preview-error">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="w-4 h-4" /> Could not load email preview
          </div>
          <div className="mt-1 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="p-6 text-slate-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-email-preview">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Email templates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Preview and send-test every ConfirmPh transactional email.
            Mock environment shows what will be sent: Postmark is only used
            when a real API key is configured.
          </p>
        </div>
        <ProviderBadge diag={catalog.email_provider}
                       validation={catalog.config_validation}
                       brand={catalog.brand} />
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT: list */}
        <div className="col-span-12 md:col-span-4 lg:col-span-3">
          <div className="rounded-lg border bg-white">
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input
                  data-testid="email-search"
                  className="pl-8 h-9"
                  placeholder="Search templates…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <select
                  data-testid="email-category-filter"
                  className="flex-1 h-8 border rounded-md px-2 bg-white"
                  value={filterCat}
                  onChange={(e) => setFilterCat(e.target.value)}
                >
                  <option value="all">All categories ({catalog.templates.length})</option>
                  {Object.entries(catalog.categories).map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {filtered.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setSelectedKey(t.key)}
                  data-testid={`email-template-${t.key.replace(/\./g, "-")}`}
                  className={`w-full text-left px-3 py-2 border-b text-sm transition ${
                    selectedKey === t.key
                      ? "bg-emerald-50 border-l-2 border-l-emerald-600"
                      : "hover:bg-slate-50"
                  }`}
                >
                  <div className="font-medium text-slate-900">{t.label}</div>
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">{t.key}</div>
                  {t.marketing_only && (
                    <Badge variant="outline"
                            className="mt-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                      Marketing: opt-in required
                    </Badge>
                  )}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-sm text-slate-500">No templates match.</div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: preview + test-send */}
        <div className="col-span-12 md:col-span-8 lg:col-span-9 space-y-4">
          {/* Preview tabs */}
          <div className="rounded-lg border bg-white">
            <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm">
                <div className="font-semibold text-slate-900">
                  {details?.label || selectedKey || "-"}
                </div>
                {details?.subject && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="text-slate-400">Subject:</span> {details.subject}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {PREVIEW_MODES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setPreviewMode(m.id)}
                      data-testid={`preview-mode-${m.id}`}
                      className={`text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 ${
                        previewMode === m.id
                          ? "bg-emerald-600 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-3 bg-slate-50">
              {loading && (
                <div className="p-8 text-slate-500 flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Rendering…
                </div>
              )}
              {!loading && previewMode === "html" && selectedKey && (
                <iframe
                  data-testid="email-preview-iframe"
                  title="Email HTML preview"
                  srcDoc={htmlPreview}
                  className="w-full bg-white rounded-md border"
                  style={{ height: "640px" }}
                />
              )}
              {!loading && previewMode === "text" && details?.text && (
                <pre data-testid="email-preview-text"
                      className="text-xs whitespace-pre-wrap p-4 bg-white border rounded-md font-mono text-slate-800">
                  {details.text}
                </pre>
              )}
              {!loading && previewMode === "json" && details && (
                <div className="text-xs space-y-3 p-4 bg-white border rounded-md">
                  <div>
                    <div className="text-slate-500 font-semibold mb-1">Subject</div>
                    <div className="font-mono">{details.subject}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 font-semibold mb-1">Sample placeholders</div>
                    <pre className="font-mono bg-slate-50 border rounded p-2 overflow-x-auto">
                      {JSON.stringify(details.sample_kwargs, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Test send */}
          <div className="rounded-lg border bg-white">
            <div className="px-4 py-3 border-b">
              <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                <Send className="w-4 h-4 text-emerald-700" /> Send test email
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Uses the live provider config. In mock mode this is a no-op (still logged).
                In Postmark mode this places a real send to the address below.
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="test-send-to"
                  type="email"
                  placeholder="recipient@example.com"
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  className="flex-1"
                />
                <Button
                  data-testid="test-send-button"
                  onClick={doSend}
                  disabled={!sendTo || !selectedKey || sending}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                  Send test
                </Button>
              </div>
              {details?.marketing_only && (
                <label className="flex items-center gap-2 text-xs text-amber-700">
                  <input type="checkbox"
                         data-testid="allow-marketing"
                         checked={allowMarketing}
                         onChange={(e) => setAllowMarketing(e.target.checked)} />
                  Marketing template: confirm I want to send this test send anyway.
                </label>
              )}
              {sendResult && (
                <SendResult result={sendResult} />
              )}
            </div>
          </div>

          {/* Provider diagnostics */}
          <ProviderDiagnosticsCard diag={catalog.email_provider}
                                    validation={catalog.config_validation} />
        </div>
      </div>
    </div>
  );
}

function ProviderBadge({ diag, validation, brand }) {
  const active = diag?.active_provider || "mock";
  const ok = active === "postmark" && validation?.ok;
  return (
    <div data-testid="provider-badge"
         className={`rounded-md border px-3 py-2 text-xs ${
           ok ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-800"
         }`}>
      <div className="font-semibold flex items-center gap-1.5">
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
        Provider: {active}
      </div>
      <div className="mt-1">
        From: <span className="font-mono">{brand?.from_name} &lt;{brand?.from_email}&gt;</span>
      </div>
      <div>Reply-to: <span className="font-mono">{brand?.reply_to}</span></div>
    </div>
  );
}

function ProviderDiagnosticsCard({ diag, validation }) {
  if (!diag) return null;
  const items = [
    ["Active provider", diag.active_provider],
    ["Requested provider", diag.requested_provider],
    ["POSTMARK_API_KEY configured", diag.postmark_api_key_configured ? "yes" : "no"],
    ["EMAIL_FROM_ADDRESS configured", diag.email_from_address_configured ? "yes" : "no"],
    ["EMAIL_FROM_NAME configured", diag.email_from_name_configured ? "yes" : "no"],
    ["EMAIL_REPLY_TO configured", diag.email_reply_to_configured ? "yes" : "no"],
    ["Message stream", diag.message_stream],
    ["Webhook required", diag.webhook_required ? "yes" : "no"],
  ];
  return (
    <div className="rounded-lg border bg-white">
      <div className="px-4 py-3 border-b">
        <div className="font-semibold text-sm text-slate-900">Email provider diagnostics</div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 py-0.5">
            <span className="text-slate-500">{k}</span>
            <span className="font-mono text-slate-800">{String(v ?? "-")}</span>
          </div>
        ))}
      </div>
      {!validation?.ok && validation?.warnings?.length > 0 && (
        <div className="px-4 py-3 border-t bg-amber-50 text-amber-800 text-xs">
          <div className="font-semibold mb-1">Configuration warnings</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function SendResult({ result }) {
  const ok = result?.ok;
  return (
    <div data-testid="send-result"
         className={`text-xs rounded-md px-3 py-2 ${
           ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : "bg-red-50 border border-red-200 text-red-800"
         }`}>
      <div className="font-semibold">
        {ok ? "Send dispatched" : "Send failed"}: status: {result.status || "unknown"}
      </div>
      {result.provider_message_id && (
        <div className="mt-1">Postmark MessageID: <span className="font-mono">{result.provider_message_id}</span></div>
      )}
      {result.error_message && (
        <div className="mt-1">Error: <span className="font-mono">{result.error_message}</span></div>
      )}
    </div>
  );
}
